import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box,
    Button,
    TextField,
    Typography
} from '@mui/material';
import { Download } from '@mui/icons-material';

import { Popup } from '../CommonComponents';
import { formatDateTime } from '../../utils/parsing';
import toastMessage from '../../utils/toast';
import { s3Upload } from '../../api-admin/s3-upload';
import {
    ADMIN_ORDER_FILE_ACCEPT,
    AdminOrderFile,
    adminOrderFileClientError,
    adminOrderFileMimeType,
    confirmAdminOrderFile,
    downloadAdminOrderFile,
    hardDeleteAdminOrderFileGroup,
    listAdminOrderFiles,
    restoreAdminOrderFileGroup,
    presignAdminOrderFile
} from '../../api-admin/order-files';

function formatBytes(bytes: number) {
    if (bytes < 1024) {
        return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
        return `${Math.round(bytes / 1024)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function uploaderLabel(file: AdminOrderFile) {
    return file.uploadedByUserName
        ? `${file.uploadedByTenantName}, ${file.uploadedByUserName}`
        : file.uploadedByTenantName;
}

export default function OrderFiles() {
    const { id } = useParams() as unknown as { id: string };
    const [files, setFiles] = useState<AdminOrderFile[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [pendingGroupId, setPendingGroupId] = useState<string | null>(null);
    const [pendingRestoreGroupId, setPendingRestoreGroupId] = useState<string | null>(null);
    const [updating, setUpdating] = useState<AdminOrderFile | null>(null);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [fileInputKey, setFileInputKey] = useState(0);

    const load = useCallback(async () => {
        try {
            setIsLoading(true);
            setFiles(await listAdminOrderFiles(id));
        } catch (error) {
            toastMessage(error, 'Filerna kunde inte hämtas.');
        } finally {
            setIsLoading(false);
        }
    }, [id]);

    useEffect(() => {
        load();
    }, [load]);

    const groups = useMemo(() => {
        const grouped = new Map<string, AdminOrderFile[]>();
        files.forEach((file) => {
            const rows = grouped.get(file.groupId) ?? [];
            rows.push(file);
            grouped.set(file.groupId, rows);
        });
        return Array.from(grouped.entries());
    }, [files]);

    const openDownload = async (fileId: number) => {
        const opened = window.open('', '_blank');
        try {
            const { presignedUrl } = await downloadAdminOrderFile(id, fileId);
            if (opened) {
                opened.location.href = presignedUrl;
            } else {
                window.location.href = presignedUrl;
            }
        } catch (error) {
            opened?.close();
            toastMessage(error, 'Filen kunde inte laddas ner.');
        }
    };

    const startUpdate = (current: AdminOrderFile) => {
        setUpdating(current);
        setTitle(current.title);
        setDescription(current.description || '');
        setFile(null);
        setFileInputKey((currentKey) => currentKey + 1);
    };

    const closeUpdate = () => {
        if (isSaving) {
            return;
        }
        setUpdating(null);
        setTitle('');
        setDescription('');
        setFile(null);
    };

    const onUpdate = async () => {
        if (!updating) {
            return;
        }
        const trimmedTitle = title.trim();
        if (!trimmedTitle) {
            toastMessage('Rubrik krävs.');
            return;
        }
        if (!file) {
            toastMessage('Välj en fil.');
            return;
        }
        const mimeType = adminOrderFileMimeType(file);
        const clientError = adminOrderFileClientError(file);
        if (!mimeType || clientError) {
            toastMessage(clientError || 'Filtypen stöds inte. Välj PDF, PNG, JPG, JPEG eller Office (doc, docx, xls, xlsx).');
            return;
        }

        setIsSaving(true);
        try {
            const body = {
                mimeType,
                originalFilename: file.name,
                title: trimmedTitle,
                description: description.trim()
            };
            const { presignedUrl, s3Key } = await presignAdminOrderFile(id, updating.groupId, body);
            await s3Upload({ file, presignedUrl, mimeType });
            await confirmAdminOrderFile(id, updating.groupId, { ...body, s3Key });
            setUpdating(null);
            setTitle('');
            setDescription('');
            setFile(null);
            await load();
            toastMessage('Ny version uppladdad.');
        } catch (error) {
            const message = error instanceof Error ? error.message : '';
            toastMessage(message || error, 'Filen kunde inte uppdateras.');
        } finally {
            setIsSaving(false);
        }
    };

    const onRestore = async () => {
        if (!pendingRestoreGroupId) {
            return;
        }
        setIsSaving(true);
        try {
            await restoreAdminOrderFileGroup(id, pendingRestoreGroupId);
            setPendingRestoreGroupId(null);
            await load();
            toastMessage('Filen är återställd.');
        } catch (error) {
            toastMessage(error, 'Filen kunde inte återställas.');
        } finally {
            setIsSaving(false);
        }
    };

    const onHardDelete = async () => {
        if (!pendingGroupId) {
            return;
        }
        setIsDeleting(true);
        try {
            await hardDeleteAdminOrderFileGroup(id, pendingGroupId);
            setPendingGroupId(null);
            await load();
            toastMessage('Filen är permanent borttagen.');
        } catch (error) {
            toastMessage(error, 'Filen kunde inte tas bort.');
        } finally {
            setIsDeleting(false);
        }
    };

    if (isLoading && files.length === 0) {
        return <Typography sx={{ p: 2 }}>Hämtar filer...</Typography>;
    }

    if (!files.length) {
        return <Typography sx={{ p: 2 }}>Det finns inga filer.</Typography>;
    }

    return (
        <Box sx={{ p: 2 }}>
            {groups.map(([groupId, rows]) => {
                const removed = rows.every((row) => row.deletedAt);
                return (
                    <Box key={groupId} sx={{ borderBottom: 1, borderColor: 'divider', py: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 1 }}>
                            <Typography variant="subtitle1">
                                {rows[0].title}
                                {removed ? ' (mjukraderad)' : ''}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                {!removed && (
                                    <Button
                                        size="small"
                                        onClick={() => startUpdate(rows.find((row) => !row.deletedAt) ?? rows[0])}
                                        disabled={isDeleting || isSaving}
                                    >
                                        Uppdatera
                                    </Button>
                                )}
                                {removed && (
                                    <Button
                                        size="small"
                                        onClick={() => setPendingRestoreGroupId(groupId)}
                                        disabled={isDeleting || isSaving}
                                    >
                                        Återställ
                                    </Button>
                                )}
                                <Button
                                    size="small"
                                    color="error"
                                    onClick={() => setPendingGroupId(groupId)}
                                    disabled={isDeleting || isSaving}
                                >
                                    Ta bort permanent
                                </Button>
                            </Box>
                        </Box>
                        {rows.map((row) => (
                            <Box key={row.id} sx={{ py: 0.75, pl: 1 }}>
                                <Typography variant="body2">
                                    {`Version ${row.version}${row.deletedAt ? ' · mjukraderad' : ''}: ${row.title}`}
                                </Typography>
                                {row.description && (
                                    <Typography variant="body2" color="text.secondary">
                                        {row.description}
                                    </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary" display="block">
                                    {uploaderLabel(row)}
                                    {' · '}
                                    {formatDateTime(row.createdAt)}
                                    {' · '}
                                    {row.originalFilename}
                                    {' · '}
                                    {formatBytes(row.byteSize)}
                                </Typography>
                                <Button
                                    size="small"
                                    startIcon={<Download />}
                                    onClick={() => openDownload(row.id)}
                                >
                                    Ladda ner
                                </Button>
                            </Box>
                        ))}
                    </Box>
                );
            })}
            <Popup
                open={!!updating}
                title={updating ? `Uppdatera "${updating.title}"` : 'Uppdatera fil'}
                okLabel="Ladda upp ny version"
                handleOk={onUpdate}
                handleClose={closeUpdate}
                isSubmitting={isSaving}
                okDisabled={!title.trim() || !file}
                fullWidth
                maxWidth="sm"
                disableEnforceFocus
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <TextField
                        label="Rubrik"
                        value={title}
                        onChange={(event) => setTitle(event.target.value)}
                        required
                        fullWidth
                        size="small"
                        inputProps={{ maxLength: 200 }}
                        disabled={isSaving}
                    />
                    <TextField
                        label="Beskrivning"
                        value={description}
                        onChange={(event) => setDescription(event.target.value)}
                        fullWidth
                        size="small"
                        multiline
                        minRows={2}
                        inputProps={{ maxLength: 2000 }}
                        disabled={isSaving}
                    />
                    <Box>
                        <Button
                            variant="outlined"
                            component="label"
                            disabled={isSaving}
                            sx={{ position: 'relative' }}
                        >
                            Välj fil
                            <input
                                key={fileInputKey}
                                type="file"
                                accept={ADMIN_ORDER_FILE_ACCEPT}
                                onChange={(event) => setFile(event.target.files?.[0] ?? null)}
                                style={{
                                    position: 'absolute',
                                    inset: 0,
                                    opacity: 0,
                                    width: '100%',
                                    height: '100%',
                                    fontSize: '16px'
                                }}
                            />
                        </Button>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            {file ? file.name : 'Ingen fil vald'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            PDF, PNG, JPG, JPEG eller Office (doc, docx, xls, xlsx). Max 25 MB. Den tidigare versionen ligger kvar.
                        </Typography>
                    </Box>
                </Box>
            </Popup>
            <Popup
                open={!!pendingRestoreGroupId}
                title="Återställ fil"
                body="Filen blir synlig igen för båda parter, med samma versioner som innan den togs bort."
                okLabel="Återställ"
                handleOk={onRestore}
                handleClose={() => setPendingRestoreGroupId(null)}
                isSubmitting={isSaving}
            />
            <Popup
                open={!!pendingGroupId}
                title="Ta bort fil permanent"
                body="Alla versioner tas bort, och filerna raderas från lagringen om ingen rad längre pekar på dem. Det går inte att ångra."
                okLabel="Ta bort permanent"
                handleOk={onHardDelete}
                handleClose={() => setPendingGroupId(null)}
                isSubmitting={isDeleting}
            />
        </Box>
    );
}
